import apiClient, { API_BASE_URL } from './client';

export const aiChatApi = {
  /**
   * Send a chat message to the AI
   */
  chat: async (message, sessionId = null, model = null, temperature = null) => {
    const response = await apiClient.post(
      '/api/ai/chat',
      {
        message,
        sessionId,
        model,
        temperature,
      },
      { timeout: 600000 }
    );
    return response.data;
  },

  /**
   * List available Ollama models
   */
  listModels: async () => {
    const response = await apiClient.get('/api/ai/models');
    return response.data;
  },

  /**
   * Clear conversation history
   */
  clearHistory: async (sessionId) => {
    const response = await apiClient.delete(`/api/ai/chat/${sessionId}`);
    return response.data;
  },

  /**
   * Generate / edit an algorithm script with local Ollama + workbench docs.
   * Uses SSE so reverse proxies do not idle-timeout the request.
   */
  generateAlgo: async ({ prompt, script, symbols, model, history, onDelta, signal }) => {
    const token = localStorage.getItem('accessToken');
    const tradingEnv = localStorage.getItem('tradingEnv') || 'paper';
    const response = await fetch(`${API_BASE_URL}/api/ai/code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: token ? `Bearer ${token}` : '',
        'X-Alpaca-Env': tradingEnv,
      },
      credentials: 'include',
      body: JSON.stringify({ prompt, script, symbols, model, history }),
      signal,
    });

    if (!response.ok) {
      let message = `Request failed with status code ${response.status}`;
      const raw = await response.text().catch(() => '');
      try {
        const parsed = JSON.parse(raw);
        message = parsed.error || parsed.message || message;
      } catch {
        if (raw && !/^</.test(raw.trim())) message = raw.slice(0, 400);
      }
      if (response.status === 504) {
        message =
          'Gateway timed out waiting for the model. Pick a smaller local model (llama3.2 or qwen2.5-coder) and retry.';
      }
      throw new Error(message);
    }

    if (!response.body) {
      throw new Error('No response stream from the AI endpoint');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let result = null;
    let streamed = '';

    const consumeBlock = (block) => {
      const normalized = String(block || '').replace(/\r\n/g, '\n');
      const data = normalized
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n');
      if (!data) return;
      let evt;
      try {
        evt = JSON.parse(data);
      } catch {
        return;
      }
      if (evt.type === 'delta' && evt.text) {
        streamed += evt.text;
        onDelta?.(evt.text);
      }
      if (evt.type === 'done') {
        result = { success: true, data: evt.data || {} };
      }
      if (evt.type === 'error') {
        throw new Error(evt.error || 'Ollama failed to generate a script');
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      const blocks = buf.split('\n\n');
      buf = blocks.pop() || '';
      for (const block of blocks) consumeBlock(block);
    }
    if (buf.trim()) consumeBlock(buf);

    if (!result && streamed.trim()) {
      return {
        success: true,
        data: {
          script: streamed,
          notes: '',
          raw: streamed,
        },
      };
    }
    if (!result) {
      throw new Error('Ollama returned no output. Check that the selected model is pulled and supports chat.');
    }
    return result;
  },

  /**
   * Check Ollama health
   */
  checkHealth: async () => {
    const response = await apiClient.get('/api/ai/health');
    return response.data;
  },
};
